import type { FilterConfigs } from './types';
import { ASSET_TYPES } from './assetTypes';
import { getFilterConfig as getAssetTypeFilterConfig } from './assetTypeRegistry';

/**
 * DEPRECATED: This file is maintained for backward compatibility.
 * New code should use the asset type configuration system in assetTypeRegistry.ts
 *
 * Configuration for different filter types that defines how they interact with preselected filters
 * and how multiple values are combined.
 *
 * BEHAVIOR:
 * - 'replace': User selection completely replaces preselected values. Use this when the user's
 *   filter selection should override what was preselected on the page. Example: compatibility
 *   filters where selecting "Premiere Pro + MOGRT" should replace a preselected "DaVinci Resolve".
 *
 * - 'additive': User selection adds to preselected values without replacing them. Use this for
 *   filters that should work alongside preselected filters. Example: themes/styles where a user
 *   can add additional themes to the preselected category themes.
 *
 * LOGIC:
 * - 'or': Multiple values are combined with OR logic in a single condition.
 *   Example: compatibility.id:=[1,2] means "compatibility ID 1 OR 2"
 *
 * - 'and': Multiple values are combined with AND logic using separate conditions.
 *   Example: types.id:=[1] && types.id:=[2] means "type ID 1 AND type ID 2"
 *
 * EXAMPLES:
 * - Compatibility filters use 'replace' + 'or' because users want to select specific software
 *   compatibility options that should replace any preselected ones.
 * - Theme/Style filters use 'additive' + 'and' because users want to add additional filters
 *   on top of the category's preselected filters.
 */
export const filterConfigs: FilterConfigs = {
  'compatibility.id': {
    behavior: 'replace',
    logic: 'or',
  },
  'types.id': {
    behavior: 'additive',
    logic: 'and',
  },
  'themes.id': {
    behavior: 'additive',
    logic: 'and',
  },
  'styles.id': {
    behavior: 'additive',
    logic: 'and',
  },
  is_lut: {
    behavior: 'additive',
    logic: 'and',
  },
};

/**
 * Default configuration for filters not explicitly defined above
 */
export const defaultFilterConfig = {
  behavior: 'additive' as const,
  logic: 'and' as const,
};

/**
 * Get configuration for a specific filter category
 * @deprecated Use getFilterConfig from assetTypeRegistry.ts with asset type context
 */
export const getFilterConfig = (category: string) => {
  // For backward compatibility, default to motion graphics configuration
  try {
    return getAssetTypeFilterConfig(ASSET_TYPES.MOTION_GRAPHICS, category);
  } catch {
    return filterConfigs[category.toLowerCase()] || defaultFilterConfig;
  }
};
